import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";

import StartPage from "./pages/StartPage";
import LandingPage from "./pages/LandingPage";
import AccessRequest from "./pages/AccessRequest";
import ClubAccess from "./pages/ClubAccess";
import NewTournament from "./pages/NewTournament";
import MyTournaments from "./pages/MyTournaments";
import Players from "./pages/MyPlayers";
import Tournament from "./pages/TournamentOverview/Content/Tournament";
import LiveScorecard from "./pages/LiveScorecard";
import LiveSpectator from "./pages/LiveSpectator";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ResetPassword from "./pages/ResetPassword";
import Admin from "./pages/Admin";
import Courses from "./pages/Courses";
import AddCourse from "./pages/AddCourse";

import Header from "./components/header";

import {
  AuthProvider,
  useAuth,
} from "./hooks/useAuth";

function PageLoader({
  message = "Förbereder HoleInOne…",
}) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#f3f0e7] px-4 text-[#1f2d26]">
      <div className="flex items-center gap-3 rounded-2xl border border-[#d8d2c4] bg-[#fffdf8] px-5 py-4 shadow-sm">
        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#e5ede7] text-[#183d2d]">
          ⛳
        </span>

        <div>
          <p className="text-sm font-semibold">
            Laddar HoleInOne
          </p>

          <p className="text-xs text-[#68716c]">
            {message}
          </p>
        </div>
      </div>
    </div>
  );
}

function RootPage() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <PageLoader message="Kontrollerar din session…" />
    );
  }

  return user ? (
    <StartPage />
  ) : (
    <LandingPage />
  );
}

function ProtectedRoute({
  children,
}) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <PageLoader message="Kontrollerar din behörighet…" />
    );
  }

  if (!user) {
    return (
      <Navigate
        to="/login"
        replace
        state={{
          from: location.pathname,
        }}
      />
    );
  }

  return children;
}

function AppWrapper() {
  const location = useLocation();
  const { user, loading } = useAuth();

  const isLiveScorecard =
    location.pathname.startsWith(
      "/live-scorekort/"
    );

  const isLiveSpectator =
    location.pathname.startsWith(
      "/live-resultat/"
    );

  const isPublicRoot =
    location.pathname === "/" &&
    (!user || loading);

  const hideHeader =
    [
      "/login",
      "/register",
      "/ansok-beta",
      "/golfklubb",
      "/aterstall-losenord",
    ].includes(location.pathname) ||
    isLiveScorecard ||
    isLiveSpectator ||
    isPublicRoot;

  return (
    <>
      {!hideHeader && <Header />}

      <Routes>
        <Route
          path="/"
          element={<RootPage />}
        />

        <Route
          path="/ny"
          element={
            <ProtectedRoute>
              <NewTournament />
            </ProtectedRoute>
          }
        />

        <Route
          path="/mina"
          element={
            <ProtectedRoute>
              <MyTournaments />
            </ProtectedRoute>
          }
        />

        <Route
          path="/spelare"
          element={
            <ProtectedRoute>
              <Players />
            </ProtectedRoute>
          }
        />

        <Route
          path="/courses"
          element={
            <ProtectedRoute>
              <Courses />
            </ProtectedRoute>
          }
        />

        <Route
          path="/courses/new"
          element={
            <ProtectedRoute>
              <AddCourse />
            </ProtectedRoute>
          }
        />

        <Route
          path="/turnering/:id"
          element={<Tournament />}
        />

        <Route
          path="/live-scorekort/:token"
          element={<LiveScorecard />}
        />

        <Route
          path="/live-resultat/:token"
          element={<LiveSpectator />}
        />

        <Route
          path="/login"
          element={<Login />}
        />

        <Route
          path="/register"
          element={<Register />}
        />

        <Route
          path="/ansok-beta"
          element={<AccessRequest />}
        />

        <Route
          path="/golfklubb"
          element={<ClubAccess />}
        />

        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <Admin />
            </ProtectedRoute>
          }
        />

        <Route
          path="/beta-admin"
          element={<Navigate to="/admin" replace />}
        />


        <Route
          path="/aterstall-losenord"
          element={<ResetPassword />}
        />

        <Route
          path="*"
          element={
            <Navigate
              to="/"
              replace
            />
          }
        />
      </Routes>
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppWrapper />
      </Router>
    </AuthProvider>
  );
}

export default App;